import { deletePromptTemplate, upsertPromptTemplate } from 'actions/promptTemplates';
import ConfirmModal from 'components/ConfirmModal';
import Modal from 'components/Modal';
import SelectRedesign from 'components/Select/SelectRedesign';
import { promptBlockTypeOptions } from 'constants/entities';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { getLanguageMatcherFromSelectedIdentifier } from 'selectors/identifier';
import { useSelector } from 'store';
import { LLMPromptTemplate } from 'types';

type ComponentProps = {
  isModalOpen: boolean;
  toggleModalOpen: () => void;
  template: LLMPromptTemplate | null;
  handleCreateBlock: (template: Partial<LLMPromptTemplate> & { parent?: string }) => void;
};

const CreateOrEditBlockTemplate: React.FC<ComponentProps> = ({
  template,
  isModalOpen,
  toggleModalOpen,
  handleCreateBlock,
}) => {
  const dispatch = useDispatch();

  const [kind, setKind] = useState(template?.kind || '');
  const [livesUnder, setLivesUnder] = useState('');

  const [isDeleting, setIsDeleting] = useState(false);

  const languageMatchers = useSelector((state) =>
    getLanguageMatcherFromSelectedIdentifier(state, '', false)
  );

  const { register, getValues, handleSubmit, watch } = useForm<{
    name: string;
    type: string;
    pattern: string;
  }>({
    defaultValues: {
      name: template?.name,
      pattern: template?.pattern,
      type: template?.kind,
    },
  });

  const onSubmit = (): void => {
    const { name, pattern } = getValues();

    handleCreateBlock({
      ...(template?.uuid ? { uuid: template?.uuid } : {}),
      name,
      pattern,
      kind: kind === 'secondary' ? 'text' : kind,
      parent: livesUnder,
    });
    toggleModalOpen();
  };

  const handleSaveButton = (): void => {
    handleSubmit(onSubmit)();
  };

  const onSaveSecondary = (): void => {
    const { name, pattern } = getValues();

    dispatch(
      upsertPromptTemplate({
        ...(template?.uuid ? { uuid: template?.uuid } : {}),
        name,
        pattern,
        kind: kind === 'secondary' ? 'text' : kind,
      })
    );
  };

  const handleSaveSecondary = (): void => {
    handleSubmit(onSaveSecondary)();
  };

  const toggleConfirmModal = (): void => {
    setIsDeleting(!isDeleting);
  };

  const handleDelete = (): void => {
    if (template) {
      dispatch(deletePromptTemplate({ uuid: template.uuid }));
      toggleModalOpen();
    }
  };

  const handleChangeKind = (value: string): void => {
    setKind(value);
  };

  const handleChangeLivesUnder = (value: string): void => {
    setLivesUnder(value);
  };

  const livesUnderOptions = languageMatchers
    .filter((lm) => !lm.parent)
    .map((lm) => ({ label: lm.name, value: lm.uuid }));

  const body = (
    <div className="mt-8 flex flex-col mb-4 h-full text-body">
      <div className="flex flex-1 flex-col gap-2">
        <div className="flex flex-row gap-4">
          <label htmlFor="feature" className="flex w-1/2 flex-col gap-1 text-body">
            <div className="flex flex-row">
              <span>Block name</span>
              <span className="text-litlingo-alert">*</span>
            </div>
            <input
              name="name"
              id="name"
              type="text"
              ref={register({ required: true, pattern: /\S/i })}
              data-testid="matcher-feature-input"
              className="form-input h-9 py-2 px-4 border border-litlingo-gray-2 rounded-lg text-body focus:outline-none"
              pattern=".*\S.*"
            />
          </label>

          <div className="w-1/2 flex flex-col gap-1 text-body">
            <div className="flex flex-row">
              <span>Type</span>
              <span className="text-litlingo-alert">*</span>
            </div>
            <SelectRedesign
              onChange={(value): void => {
                handleChangeKind(value);
              }}
              options={promptBlockTypeOptions}
              value={kind}
              className="h-9"
            />
          </div>
        </div>
        {kind === 'secondary' && (
          <div className="w-1/2 flex flex-col gap-1 text-body">
            <div className="flex flex-row">
              <span>Lives under</span>
              <span className="text-litlingo-alert">*</span>
            </div>
            <SelectRedesign
              onChange={(value): void => {
                handleChangeLivesUnder(value);
              }}
              options={livesUnderOptions}
              value={livesUnder}
              className="h-9"
            />
          </div>
        )}
        <label htmlFor="feature" className="flex flex-col gap-1 text-body">
          <div className="flex flex-row">
            <span>Pattern Placeholder Text</span>
            <span className="text-litlingo-alert">*</span>
          </div>

          <textarea
            id="pattern"
            name="pattern"
            ref={register({ required: true, pattern: /\S/i })}
            rows={5}
            placeholder="Elements that make up the risk model. ie. Core corruption risk model = Value +Decision+Authority+ influence intent"
            className="form-input w-full p-2 text-body rounded-md resize-none placeholder-italic placeholder-litlingo-gray-6 border-litlingo-gray-2"
          />
        </label>
      </div>
    </div>
  );

  return (
    <>
      {isModalOpen && (
        <Modal
          title={template ? 'Edit Prompt Block' : 'New Prompt Block'}
          body={body}
          okButton
          okButtonText={template ? 'Save & Add' : 'Create'}
          okButtonDisabled={!watch('name') || !kind || !watch('pattern')}
          okButtonOnClick={handleSaveButton}
          toggleShowModal={toggleModalOpen}
          style={{ width: '37.5rem' }}
          cancelButton
          cancelButtonText="Save"
          cancelButtonOnclick={handleSaveSecondary}
          secondaryButton
          secondaryButtonText="Delete block"
          secondaryButtonStyle="text-litlingo-alert underline focus:outline-none"
          secondaryButtonOnclick={toggleConfirmModal}
          xButton
        />
      )}
      {isDeleting && (
        <ConfirmModal
          text="Are you sure you want to delete this Prompt Block?"
          okButtonText="Yes"
          cancelButtonText="No"
          data-testid="delete-modal"
          okButtonOnClick={handleDelete}
          toggleShowModal={toggleConfirmModal}
        />
      )}
    </>
  );
};

export default CreateOrEditBlockTemplate;
